import { Request } from 'api';
import { AxiosResponse } from 'axios';
import { CreatePodParams, Pod, PodEventHistory, UpdatePodParams } from './pods.types';

interface PaginatedApiResponse {
    data: Array<Pod>;
    links: any; // Replace 'any' with the actual type of 'links'
    meta: any; // Replace 'any' with the actual type of 'meta'
}

type ReturnTypeCollection = Promise<AxiosResponse<PaginatedApiResponse>>;
type ReturnTypeHistory = Promise<AxiosResponse<Array<PodEventHistory>>>;
type PodState = 'all' | 'free' | 'transferred' | 'assigned' | 'pending_transfer';
type CustodyStatus =
    | 'all'
    | 'in_warehouse'
    | 'shipped'
    | 'return_requested'
    | 'returned'
    | 'returning'
    | 'problem_pods'
    | 'shipped_no_sub'
    | 'comp_no_sub';

interface GetPodsParams {
    uuid?: string;
    serial_number?: string;
    club_id?: string;
    simple_id?: string;
    organization_uuid?: string;
    athlete_user_uuid?: string;
    pod_state?: PodState;
    custody_status?: CustodyStatus;
    created_at?: string;
    updated_at?: string;
    sort?: 'serial_number' | 'created_at' | 'updated_at' | '-serial_number' | '-created_at';
    between_dates?: Array<Date | undefined>;
    page?: number;
    per_page?: number;
    has_active_sub?: boolean;
    no_active_sub?: boolean;
    is_comp?: boolean;
    active_org_sub?: boolean;
    partial_org_sub?: boolean;
    past_due?: boolean;
    past_due_in_use?: boolean;
    in_stock?: boolean;
    /** Inventory-org pods that are decommissioned (matches in_stock_decommissioned count) */
    in_stock_decommissioned?: boolean;
}

interface MarkShippedParams {
    tracking_number?: string;
    notes?: string;
}

interface CustodyNoteParams {
    notes?: string;
}

interface BulkUpdatePodsParams {
    pod_uuids: string[];
    simple_id: string;
    club_id: string;
    organization_uuid: string | null;
}

const getPods = (params?: GetPodsParams): ReturnTypeCollection => {
    const custodyFilterMap: Partial<Record<CustodyStatus, Record<string, string>>> = {
        problem_pods: { 'filter[problem_pods]': '1' },
        shipped_no_sub: { 'filter[shipped_no_sub]': '1' },
        comp_no_sub: { 'filter[comp_no_sub]': '1' },
    };

    const custodyFilter =
        params?.custody_status && params.custody_status !== 'all'
            ? custodyFilterMap[params.custody_status] ?? {
                  'filter[custody_status]': params.custody_status,
              }
            : {};

    const filterParams = {
        'filter[organization_uuid]': params?.organization_uuid,
        'filter[serial_number]': params?.serial_number,
        'filter[simple_id]': params?.simple_id,
        'filter[athlete_user_uuid]': params?.athlete_user_uuid,
        'filter[pod_state]': params?.pod_state === 'all' ? undefined : params?.pod_state,
        'filter[has_active_sub]': params?.has_active_sub ? '1' : undefined,
        'filter[no_active_sub]': params?.no_active_sub ? '1' : undefined,
        'filter[is_comp]': params?.is_comp ? '1' : undefined,
        'filter[active_org_sub]': params?.active_org_sub ? '1' : undefined,
        'filter[partial_org_sub]': params?.partial_org_sub ? '1' : undefined,
        'filter[past_due]': params?.past_due ? '1' : undefined,
        'filter[past_due_in_use]': params?.past_due_in_use ? '1' : undefined,
        'filter[in_stock]': params?.in_stock ? '1' : undefined,
        'filter[in_stock_decommissioned]': params?.in_stock_decommissioned ? '1' : undefined,
        ...custodyFilter,
        sort: params?.sort,
        //between_dates: params?.between_dates,
        page: params?.page,
        per_page: params?.per_page,
    };
    return Request.get(`admin/pods`, { params: filterParams });
};

type ReturnTypeSingle = Promise<AxiosResponse<Pod>>;

const createPod = (podData: CreatePodParams): ReturnTypeSingle => {
    return Request.post(`admin/pods`, podData);
};

const updatePod = (podUuid: string, podData: UpdatePodParams): ReturnTypeSingle => {
    return Request.put(`admin/pods/${podUuid}`, podData);
};

const changePodOrganization = (
    podUuid: string,
    organizationUuid: string | null,
): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/change-organization`, {
        organization_uuid: organizationUuid,
    });
};

const transferPodToIndividual = (podUuid: string, individualUuid: string): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/transfer-individual`, {
        individual_uuid: individualUuid,
    });
};

const assignPodToIndividual = (podUuid: string, individualUuid: string): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/assign-individual`, {
        individual_uuid: individualUuid,
    });
};

const detachPodIndividual = (podUuid: string): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/detach-individual`);
};

const getPodHistory = (podUuid: string): ReturnTypeHistory => {
    return Request.get(`admin/pods/${podUuid}/history`);
};

const getPodHistoryForAthlete = (athleteUuid: string): ReturnTypeHistory => {
    return Request.get(`admin/pods/${athleteUuid}/athlete-history`);
};

const bulkUpdatePods = (params: BulkUpdatePodsParams): ReturnTypeSingle => {
    return Request.post(`admin/pods/bulk`, params);
};

const markPodShipped = (podUuid: string, params?: MarkShippedParams): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/mark-shipped`, params ?? {});
};

const markPodReturned = (podUuid: string, params?: CustodyNoteParams): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/mark-returned`, params ?? {});
};

const requestPodReturn = (podUuid: string, params?: CustodyNoteParams): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/request-return`, params ?? {});
};

// Clear a pod's billing overrides (is_comp / external invoice / subscription / transfer)
// when it is reassigned straight to a different org without passing through stock. Custody
// and the new org/athlete association are left untouched by the backend.
const clearPodBilling = (podUuid: string, params?: CustodyNoteParams): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/clear-billing`, params ?? {});
};

const togglePodComp = (podUuid: string): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/toggle-comp`);
};

const togglePodDecommission = (podUuid: string): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/toggle-decommission`);
};

interface AddExternalInvoiceParams {
    external_invoice_id: string;
    external_invoice_notes?: string;
    external_invoice_end_date: string;
}

const addExternalInvoice = (
    podUuid: string,
    params: AddExternalInvoiceParams,
): ReturnTypeSingle => {
    return Request.post(`admin/pods/${podUuid}/external-invoice`, params);
};

const removeExternalInvoice = (podUuid: string): ReturnTypeSingle => {
    return Request.delete(`admin/pods/${podUuid}/external-invoice`);
};

interface BulkExternalInvoiceParams {
    pod_uuids: string[];
    external_invoice_id: string;
    external_invoice_notes?: string;
    external_invoice_end_date: string;
}

const bulkExternalInvoice = (params: BulkExternalInvoiceParams): Promise<AxiosResponse> => {
    return Request.post('admin/pods/bulk-external-invoice', params);
};

const bulkRemoveExternalInvoice = (pod_uuids: string[]): Promise<AxiosResponse> => {
    return Request.post('admin/pods/bulk-remove-external-invoice', { pod_uuids });
};

interface PodSubCounts {
    activeAthleteSub: number | null;
    activeOrgSub: number | null;
    partialOrgSub: number | null;
    pastDue: number | null;
    pastDueInUse: number | null;
    comp: number | null;
    inStock: number | null;
    /** Inventory-org pods that are decommissioned; excluded from inStock count */
    inStockDecommissioned: number | null;
    /** Pod-level split for the dashboard (see admin/pods/counts). */
    subscribedOrg: number | null;
    /** Subset of subscribedOrg billed via external invoice (excluded from revenue). */
    subscribedOrgExternalInvoice: number | null;
    subscribedAthlete: number | null;
    compMarked: number | null;
    freeUnmarked: number | null;
    /** Lapsed (canceled/past-due) subscriptions with pods still out — actionable. */
    pastDueOrg: number | null;
    pastDueAthlete: number | null;
    /** Counted (subscribed) pods billing $0 via a 100%-off coupon or standing credit. */
    subscribedZeroChargePods: number | null;
    /** Actual recurring monthly revenue (USD) across the counted pod subs, from Stripe. */
    actualPodMonthlyRevenue: number | null;
}

const getPodSubCounts = async (): Promise<PodSubCounts> => {
    try {
        const res = await Request.get('admin/pods/counts');
        return {
            activeAthleteSub: res.data.active_athlete_sub ?? null,
            activeOrgSub: res.data.active_org_sub ?? null,
            partialOrgSub: res.data.partial_org_sub ?? null,
            pastDue: res.data.past_due ?? null,
            pastDueInUse: res.data.past_due_in_use ?? null,
            comp: res.data.comp ?? null,
            inStock: res.data.in_stock ?? null,
            inStockDecommissioned: res.data.in_stock_decommissioned ?? null,
            subscribedOrg: res.data.subscribed_org ?? null,
            subscribedOrgExternalInvoice: res.data.subscribed_org_external_invoice ?? null,
            subscribedAthlete: res.data.subscribed_athlete ?? null,
            compMarked: res.data.comp_marked ?? null,
            freeUnmarked: res.data.free_unmarked ?? null,
            pastDueOrg: res.data.past_due_org ?? null,
            pastDueAthlete: res.data.past_due_athlete ?? null,
            subscribedZeroChargePods: res.data.subscribed_zero_charge_pods ?? null,
            actualPodMonthlyRevenue: res.data.actual_pod_monthly_revenue ?? null,
        };
    } catch {
        return {
            activeAthleteSub: null,
            activeOrgSub: null,
            partialOrgSub: null,
            pastDue: null,
            pastDueInUse: null,
            comp: null,
            inStock: null,
            inStockDecommissioned: null,
            subscribedOrg: null,
            subscribedOrgExternalInvoice: null,
            subscribedAthlete: null,
            compMarked: null,
            freeUnmarked: null,
            pastDueOrg: null,
            pastDueAthlete: null,
            subscribedZeroChargePods: null,
            actualPodMonthlyRevenue: null,
        };
    }
};

/** One organization's pod coverage split (admin/pods/org-coverage). */
interface PodOrgCoverageRow {
    organization_uuid: string;
    organization_name: string | null;
    /** Stripe customer id (cus_…) for an "open in Stripe" link; may be null. */
    stripe_customer_id: string | null;
    total_pods: number;
    paid_pods: number;
    external_invoice_pods: number;
    comped_pods: number;
    free_pods: number;
    past_due_pods: number;
}

interface PodOrgCoverageResponse {
    data: PodOrgCoverageRow[];
    totals: {
        paid_pods: number;
        external_invoice_pods: number;
        comped_pods: number;
        free_pods: number;
        past_due_pods: number;
        total_pods: number;
        organization_count: number;
    };
}

const getPodOrgCoverage = (): Promise<AxiosResponse<PodOrgCoverageResponse>> =>
    Request.get('admin/pods/org-coverage');

/** One subscribed athlete's pod count (admin/pods/athlete-coverage). */
interface PodAthleteCoverageRow {
    individual_uuid: string | null;
    athlete_name: string | null;
    organization_uuid: string | null;
    organization_name: string | null;
    /** Athlete's Stripe subscription id (sub_…) for an "open in Stripe" link; may be null. */
    stripe_subscription_id: string | null;
    pods: number;
}

interface PodAthleteCoverageResponse {
    data: PodAthleteCoverageRow[];
    totals: {
        athletes: number;
        pods: number;
        organization_count: number;
    };
}

const getPodAthleteCoverage = (): Promise<AxiosResponse<PodAthleteCoverageResponse>> =>
    Request.get('admin/pods/athlete-coverage');

/** Per-athlete breakdown of past-due (lapsed-subscription) pods. Same shape as athlete-coverage. */
const getPodPastDueAthleteCoverage = (): Promise<AxiosResponse<PodAthleteCoverageResponse>> =>
    Request.get('admin/pods/past-due-athlete-coverage');

export {
    createPod,
    getPods,
    updatePod,
    changePodOrganization,
    transferPodToIndividual,
    assignPodToIndividual,
    detachPodIndividual,
    getPodHistory,
    getPodHistoryForAthlete,
    bulkUpdatePods,
    markPodShipped,
    markPodReturned,
    requestPodReturn,
    clearPodBilling,
    togglePodComp,
    togglePodDecommission,
    addExternalInvoice,
    removeExternalInvoice,
    bulkExternalInvoice,
    bulkRemoveExternalInvoice,
    getPodSubCounts,
    getPodOrgCoverage,
    getPodAthleteCoverage,
    getPodPastDueAthleteCoverage,
};
export type {
    GetPodsParams,
    PodState,
    CustodyStatus,
    PodSubCounts,
    PodOrgCoverageRow,
    PodOrgCoverageResponse,
    PodAthleteCoverageRow,
    PodAthleteCoverageResponse,
};
